import { Logger } from '@futrli/shared-services';

import { checkIfVisitorIdIsValid } from './utils';

const VISITOR_ID_CHECK_TIME = 200;

interface PendoEvent {
  eventName: string;
  properties?: Record<string, string | number | boolean>;
}

// Event and Page View Queues
const eventQueue: PendoEvent[] = [];
const pageViewQueue: string[] = [];

// State for Monitoring Visitor ID
let isMonitoringEvents = false;
let isMonitoringPageViews = false;

// Event Queue Processing
const processEventQueue = () => {
  if (!checkIfVisitorIdIsValid()) {
    return;
  }

  const queueCopy = [...eventQueue];
  eventQueue.length = 0; // Clear the original queue

  queueCopy.forEach(({ eventName, properties }) => {
    triggerPendoEvent(eventName, properties);
  });
};

// Page View Queue Processing
const processPageViewQueue = () => {
  if (!checkIfVisitorIdIsValid()) {
    return;
  }

  const queueCopy = [...pageViewQueue];
  pageViewQueue.length = 0; // Clear the original queue

  queueCopy.forEach(url => {
    triggerPendoPageView(url);
  });
};

// Internal Function to Trigger a Pendo Event
const triggerPendoEvent = (eventName: string, properties?: Record<string, string | number | boolean>) => {
  if (window.pendo?.track) {
    window.pendo.track(eventName, properties);
  } else {
    Logger.error('[Pendo Debug] Failed to track event.');
  }
};

// Internal Function to Trigger a Pendo Page View
const triggerPendoPageView = (url: string) => {
  if (window.pendo?.pageLoad) {
    window.pendo.pageLoad(url);
  } else {
    Logger.error('[Pendo Debug] Failed to track page view.');
  }
};

// Track Custom Events
export const trackPendoEvent = (eventName: string, properties?: Record<string, string | number | boolean>) => {
  if (window.location.hostname === 'localhost') {
    return;
  }
  if (checkIfVisitorIdIsValid() && window.pendo?.guidesPayload) {
    triggerPendoEvent(eventName, properties);
    processEventQueue();
  } else {
    Logger.warn(`[Pendo Debug] Visitor ID not valid. Queuing event`);
    eventQueue.push({ eventName, properties });
    monitorEventQueue();
  }
};

// Track Page Views
export const trackPageView = () => {
  if (window.location.hostname === 'localhost') {
    return;
  }
  const currentUrl = window.location.href;

  if (checkIfVisitorIdIsValid() && window.pendo?.guidesPayload) {
    triggerPendoPageView(currentUrl);
    processPageViewQueue();
  } else {
    Logger.warn(`[Pendo Debug] Visitor ID not valid. Queuing page view`);
    pageViewQueue.push(currentUrl);
    monitorPageViewQueue();
  }
};

// Monitor Event Queue
const monitorEventQueue = () => {
  if (isMonitoringEvents) {
    return;
  }

  isMonitoringEvents = true;

  const checkInterval = setInterval(() => {
    if (checkIfVisitorIdIsValid()) {
      clearInterval(checkInterval);
      isMonitoringEvents = false;
      processEventQueue();
    }
  }, VISITOR_ID_CHECK_TIME);
};

// Monitor Page View Queue
const monitorPageViewQueue = () => {
  if (isMonitoringPageViews) {
    return;
  }

  isMonitoringPageViews = true;

  const checkInterval = setInterval(() => {
    if (checkIfVisitorIdIsValid()) {
      clearInterval(checkInterval);
      isMonitoringPageViews = false;
      processPageViewQueue();
    }
  }, VISITOR_ID_CHECK_TIME);
};
